import React, { useState } from "react";
import { toast } from "sonner";
import { Save, User, Building2, Receipt, MapPin, LogOut, Shield, Mail } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    gstin: user?.gstin || "",
    address: user?.address || "",
    billing_address: user?.billing_address || "",
    email: user?.email || "",
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateGstin = (g) => !g || /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(g);

  const save = async (e) => {
    e.preventDefault();
    if (!validateGstin(form.gstin.trim())) {
      toast.error("Invalid GSTIN format");
      return;
    }
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="border-b border-zinc-900 bg-white px-6 lg:px-10 py-10">
        <div className="tag">ACCOUNT</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
          Your profile
        </h1>
        <p className="text-zinc-500 mt-2">
          Company details used on GST invoices and shipping.
        </p>
      </div>

      <div className="px-6 lg:px-10 py-10 grid lg:grid-cols-3 gap-6">
        <form
          onSubmit={save}
          className="lg:col-span-2 bg-white border-2 border-zinc-900 p-6 space-y-5"
          data-testid="profile-form"
        >
          <div>
            <label className="tag flex items-center gap-1 mb-2"><User className="w-3 h-3" /> AUTHORISED PERSON</label>
            <input
              data-testid="profile-name"
              value={form.name}
              onChange={onChange("name")}
              className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3"
            />
          </div>

          <div>
            <label className="tag flex items-center gap-1 mb-2"><Building2 className="w-3 h-3" /> FIRM NAME</label>
            <input
              data-testid="profile-company"
              value={form.company}
              onChange={onChange("company")}
              placeholder="Acme Polymers Pvt Ltd"
              className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3"
            />
          </div>

          <div>
            <label className="tag flex items-center gap-1 mb-2"><Mail className="w-3 h-3" /> EMAIL FOR INVOICES</label>
            <input
              data-testid="profile-email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              placeholder="procurement@yourcompany.com"
              className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3"
            />
            <p className="text-xs text-zinc-500 mt-1 font-mono-data">
              GST invoice PDF is emailed here after every paid order.
            </p>
          </div>

          <div>
            <label className="tag flex items-center gap-1 mb-2"><Receipt className="w-3 h-3" /> GSTIN</label>
            <input
              data-testid="profile-gstin"
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
              placeholder="27AAAPL1234C1Z5"
              maxLength={15}
              className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3 font-mono-data uppercase tracking-widest"
            />
            <p className="text-xs text-zinc-500 mt-1 font-mono-data">
              15-character GSTIN — appears on every tax invoice.
            </p>
          </div>

          <div>
            <label className="tag flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" /> BILLING ADDRESS</label>
            <textarea
              data-testid="profile-billing-address"
              value={form.billing_address}
              onChange={onChange("billing_address")}
              rows={3}
              placeholder="Registered office address (appears on GST invoice)"
              className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3"
            />
          </div>

          <div>
            <label className="tag flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" /> DEFAULT DELIVERY ADDRESS</label>
            <textarea
              data-testid="profile-address"
              value={form.address}
              onChange={onChange("address")}
              rows={3}
              placeholder="Factory / warehouse address"
              className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-4 py-3"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="save-profile-button"
            className="bg-[#FF5A00] text-white px-6 py-3 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> {saving ? "SAVING..." : "SAVE CHANGES"}
          </button>
        </form>

        {/* Side panel */}
        <div className="space-y-4">
          <div className="bg-white text-zinc-900 p-6 border-2 border-zinc-900 hard-shadow-sm">
            <div className="tag text-[#FF5A00]">SIGNED IN AS</div>
            <div className="font-mono-data text-2xl font-bold mt-2">+{user?.phone}</div>
            <div className="text-sm text-zinc-500 mt-1 truncate">{user?.name}</div>
            {user?.role === "admin" && (
              <div className="mt-3 inline-flex items-center gap-1 border-2 border-[#FF5A00] px-2 py-1 font-mono-data text-[10px] tracking-widest text-[#FF5A00]">
                <Shield className="w-3 h-3" /> ADMIN
              </div>
            )}
          </div>

          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="admin-panel-link"
              className="block bg-white border-2 border-zinc-900 p-5 hard-shadow-sm hover:bg-zinc-50"
            >
              <div className="tag">ADMIN</div>
              <div className="font-display font-bold text-lg mt-1">Open admin panel →</div>
              <div className="text-xs text-zinc-500 mt-1">Manage products & inventory</div>
            </Link>
          )}

          <button
            onClick={() => {
              logout();
              nav("/login");
            }}
            data-testid="profile-logout-button"
            className="w-full flex items-center justify-center gap-2 border-2 border-zinc-900 py-3 font-mono-data text-xs tracking-widest bg-white hover:bg-zinc-900 hover:text-white"
          >
            <LogOut className="w-4 h-4" /> SIGN OUT
          </button>
        </div>
      </div>
    </div>
  );
}
